// POKERCITY DEALER CERTIFICATION — QUESTION BANK
// Track: Professional | Category: Scenario-Based Rulings
// 30 questions | All difficulty: Hard (scenario questions are inherently complex)

export const SCENARIO_QUESTIONS = [

  {
    id: 'sc_001', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: Preflop, Player A (UTG) folds. Player B raises. Player C calls. Player D, who has been texting, folds out of turn without looking at your cards. Player B then re-raises. What happens to Player D\'s out-of-turn fold?',
    options: [
      'A) Player D\'s fold stands regardless — they already acted',
      'B) The fold is binding only if no action changed; since Player B re-raised (action changed), Player D may reconsider',
      'C) Player D must fold since they touched their cards',
      'D) Player D\'s fold is voided; they receive a penalty and must act in turn'
    ],
    correct: 'B',
    explanation: 'An out-of-turn fold is binding only if the action does not change before it reaches the player. Player B\'s re-raise constitutes a change in action. Player D may reconsider their action. Additionally, the floor may assess a penalty for the out-of-turn action.'
  },

  {
    id: 'sc_002', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: The board reads 7♣ 8♣ 9♣ 10♣. Player A tables J♣ 2♦ claiming a straight flush. Player B tables A♣ K♦ claiming the nut flush. Who wins and what are the actual hands?',
    options: [
      'A) Player A wins with a straight flush (7-J of clubs)',
      'B) Player B wins with an ace-high flush (A-K-10-9-8 of clubs)',
      'C) Player A wins with a royal flush using the board',
      'D) Player B wins with a royal flush (A-K-Q-J-10 of clubs from the board)'
    ],
    correct: 'A',
    explanation: 'Player A\'s best hand: J♣ 7♣ 8♣ 9♣ 10♣ = Jack-high straight flush. Player B\'s best hand: A♣ 10♣ 9♣ 8♣ 7♣ = Ace-high flush (NOT a straight flush — the K♦ is not a club). The straight flush beats the flush. Player A wins.'
  },

  {
    id: 'sc_003', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: On the river, Player A bets 500. Player B raises to 2,000. Player A announces "I raise to 5,000" but only moves forward 3,000 chips. Player B asks "is that a full raise?" What is the binding action?',
    options: [
      'A) The action is 3,000 total — chips speak over words',
      'B) The action is 5,000 — the verbal declaration is binding; Player A must complete to 5,000',
      'C) The action is the minimum raise (4,000) since neither verbal nor chip amount is definitive',
      'D) Player A may choose between 3,000 and 5,000 since both are valid raises'
    ],
    correct: 'B',
    explanation: 'The verbal declaration of "5,000" is binding. Player A must add the remaining 2,000 chips to complete their declared raise. Verbal declarations of amount override chip placement when a specific amount is named.'
  },

  {
    id: 'sc_004', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: Three players are all-in before the flop. Player A shows A♠ A♥. Player B shows K♣ K♦. Player C shows Q♠ Q♦. The board runs: K♥ K♠ Q♥ Q♣ A♦. Who wins what?',
    options: [
      'A) Player A wins everything with aces full of kings',
      'B) Player B wins everything with four kings',
      'C) Player B wins the main pot with four kings; Player A and C split the side pots',
      'D) Player B wins all pots with four kings (best hand)'
    ],
    correct: 'D',
    explanation: 'Player B: K♣ K♦ K♥ K♠ A♦ = Four Kings with Ace kicker. Player A: A♠ A♥ A♦ K♥ K♠ = Full House (Aces full of Kings). Player C: Q♠ Q♦ Q♥ Q♣ A♦ = Four Queens. Four Kings beats Four Queens beats Full House. Player B wins all pots they are eligible for.'
  },

  {
    id: 'sc_005', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: Player A is the big blind. Everyone folds to the small blind who calls. Player A checks their option. The flop is dealt. The small blind leads out with a bet. Player A calls. The turn comes and the small blind checks. Player A bets. The small blind raises. Player A tanks for five minutes. Another player calls for a clock. What happens next?',
    options: [
      'A) The dealer starts a 60-second count immediately',
      'B) The dealer notifies the floor; the floor gives Player A a set time (typically 60 seconds) with a countdown',
      'C) Player A is given unlimited time since they were called; clocks only apply to first decisions',
      'D) The hand is folded immediately since more than two minutes elapsed'
    ],
    correct: 'B',
    explanation: 'Any player at the table (except the player facing the decision) may call a clock. The dealer notifies the floor. The floor gives the player a reasonable time (typically 60 seconds with a 10-second verbal countdown). If time expires, the hand is dead.'
  },

  {
    id: 'sc_006', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: UTG moves all-in for 15,000. Three players call. You begin spreading the flop before realizing one of the callers (who has 12,000) is now also all-in since they called 15,000 with only 12,000 behind. The flop is partially spread (two cards visible). What happens?',
    options: [
      'A) The flop stands — cards already dealt cannot be taken back',
      'B) Stop action, resolve the all-in amounts and pot structure, then continue with the cards as dealt',
      'C) Declare a misdeal and reshuffle',
      'D) The partially spread cards are returned and the board is re-dealt after pot correction'
    ],
    correct: 'B',
    explanation: 'Stop action and call the floor. The pot structure must be correctly established (main pot, side pots). Once corrected, the flop cards already visible remain — they are live. The third flop card is dealt if not yet turned. Correcting the pot structure does not invalidate cards already dealt.'
  },

  {
    id: 'sc_007', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: On a board of A♥ A♦ A♣ A♠ K♥, Player A turns over K♦ K♣ announcing "full house." Player B mucks. You begin to push the pot to Player A. Another player says "wait — that\'s four of a kind with a king kicker, not a full house." What is the correct ruling?',
    options: [
      'A) Player A wins — they tabled their hand and it was the best available',
      'B) Player A wins — the announcement of "full house" was an error but cards speak',
      'C) Player A wins with four aces and a king kicker; Player B\'s muck stands',
      'D) Re-evaluate the hand; if Player B\'s mucked hand can be retrieved and beats four aces, Player B wins'
    ],
    correct: 'C',
    explanation: 'Cards speak. Player A\'s actual hand is four aces, kings full is irrelevant since there are four aces on the board. The correct hand is four aces (K♦ is the kicker — best 5-card hand is A♥A♦A♣A♠K♦). Player B voluntarily mucked. The pot stands with Player A regardless of the misannouncement.'
  },

  {
    id: 'sc_008', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: Player A and Player B are heads-up in a cash game. Player A is the dealer/button. Player B is the big blind. Preflop, Player A raises. Player B goes all-in. Player A has Player B covered and calls. Both players table hands. You begin dealing the board but realizes they only have one card left in the deck. What happened and what is the ruling?',
    options: [
      'A) Use the burn cards to complete the board',
      'B) This is impossible in a properly shuffled 52-card deck — investigate for a missing card',
      'C) Deal what is available and declare the remaining streets a push',
      'D) Reshuffle the muck and burn cards and continue dealing'
    ],
    correct: 'B',
    explanation: 'A standard 52-card deck with 2 hole cards each, 3 burn cards, and 5 community cards uses exactly 12 cards, leaving 40. Running out before the board is complete means cards are missing. The floor must investigate — do not use burn cards or reshuffle until the discrepancy is resolved.'
  },

  {
    id: 'sc_009', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: Player A raises preflop. Player B goes all-in for less than a minimum raise. Player C is in the big blind and has not yet acted. Player A calls the all-in. Player C says "I want to raise." Can Player C raise?',
    options: [
      'A) No — the action is closed since Player A called a sub-minimum all-in',
      'B) Yes — Player C has not acted yet; their option is always open regardless of raise size',
      'C) Only if Player C was not the original opener',
      'D) Yes but only to the minimum raise amount'
    ],
    correct: 'B',
    explanation: 'Player C has not yet acted in this round. The sub-minimum all-in rule only applies to players who have already acted (called). Player C, having not yet acted, retains their full range of options including raising. This is a critical distinction in the sub-minimum all-in rule.'
  },

  {
    id: 'sc_010', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: At showdown, Player A tables 5♠ 5♦. The board is A♠ A♦ A♥ A♣ 5♥. Player A says "five of a kind." What does you announce and who wins the pot?',
    options: [
      'A) "Five of a kind, Player A wins" — five of a kind beats everything',
      'B) "Four aces with a five kicker" — five of a kind does not exist in standard poker',
      'C) "The board plays" — four aces is the best hand using just the board',
      'D) "Full house, fives full of aces" — pocket fives with three aces on board'
    ],
    correct: 'B',
    explanation: 'Five of a kind does not exist in standard poker (without wild cards). Player A\'s best 5-card hand uses the four aces on the board plus their 5♠ or 5♦ as the kicker. The hand is "Four aces, five kicker." The dealer announces this correctly. If another player has a better kicker (6 through K), that player wins.'
  },

  {
    id: 'sc_011', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: The tournament is hand-for-hand on the bubble. Your table still has a hand in progress while other tables have finished and are waiting. A player at your table is acting extremely slowly on a simple decision, appearing to stall deliberately. What do you do?',
    options: [
      'A) Allow it — betting decisions are always a player\'s right',
      'B) Note the situation and if it appears deliberate stalling, call the floor for a ruling',
      'C) Call a clock on the player immediately',
      'D) Tell the player to act faster since hand-for-hand rules apply'
    ],
    correct: 'B',
    explanation: 'Deliberate stalling during hand-for-hand play is a rules violation. If the dealer suspects stalling (excessively slow betting when no real decision is being made), the floor is called. The floor can assess penalties for deliberate delay. The dealer notes the behavior without making accusations.'
  },

  {
    id: 'sc_012', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: Player A makes a large raise. Player B is counting their chips to call. Player B\'s phone buzzes and they glance at it. They then fold. Player A says "angle shoot — they looked at their phone to get a signal." What does the dealer do?',
    options: [
      'A) Reverse Player B\'s fold — the allegation must be investigated before the action stands',
      'B) Player B\'s fold stands; call the floor to note the allegation and investigate the phone use',
      'C) Confiscate Player B\'s phone and continue play',
      'D) Void the hand and reshuffle since the integrity of the hand is in question'
    ],
    correct: 'B',
    explanation: 'Player B\'s fold is binding. The floor is called to note the allegation and review the phone use (surveillance, checking the phone contents if permitted). The fold cannot be reversed based on an unproven allegation. The integrity investigation is handled separately from the hand result.'
  },

  {
    id: 'sc_013', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: You are dealing a final table being broadcast. A player who is all-in is eliminated. As they stand to leave, they expose all remaining active players\' hands to the rail (spectators). What is the correct action?',
    options: [
      'A) The hand continues — the eliminated player cannot affect the outcome',
      'B) Stop the hand, call the floor; depending on what was seen, the hand may be redealt or penalties assessed',
      'C) Ask the other players if they want to continue',
      'D) Award the pot to the player who would have won most likely'
    ],
    correct: 'B',
    explanation: 'Exposing hole cards of active players to the rail is a serious integrity violation. The hand is stopped immediately and the floor called. The floor determines whether the exposure materially affected the hand (e.g., did any remaining player see the exposed cards). Redealing or penalties may apply.'
  },

  {
    id: 'sc_014', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: The board is T♠ J♠ Q♠ K♠ on the turn. Player A bets large. Player B raises all-in. Player A says "I call — I have the nuts." Player A tables A♠ 2♣. Is Player A correct that they have the nut hand? The river is dealt: 9♠.',
    options: [
      'A) Player A had the nuts on the turn and still has the nuts on the river (royal flush)',
      'B) Player A had the nuts on the turn; after the 9♠ river, Player A still has the nut hand',
      'C) Player A had the nuts on the turn; after the 9♠ river, any player holding 8♠ would have a better straight flush',
      'D) Player A never had the nuts — a flush could have been beaten by a full house'
    ],
    correct: 'A',
    explanation: 'On the turn with T♠ J♠ Q♠ K♠ on board, A♠ is the nut flush/royal flush draw — the nuts. After the 9♠ river, the board is 9♠ T♠ J♠ Q♠ K♠. Player A holds A♠ completing the royal flush (A♠ K♠ Q♠ J♠ T♠) — still the absolute nuts. No hand can beat a royal flush.'
  },

  {
    id: 'sc_015', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: A player who speaks limited English raises and then seems confused when re-raised, putting in what appears to be a call. Another player claims it was a re-raise. The chips amount is consistent with either action. How does the dealer handle this?',
    options: [
      'A) Rule it as a call — when ambiguous, the smaller action stands',
      'B) Rule it as a raise — the player moved chips forward aggressively',
      'C) Call the floor; try to establish intent through any available means (gestures, translator if available)',
      'D) Ask other players at the table to interpret the action'
    ],
    correct: 'C',
    explanation: 'The floor is called for ambiguous actions, particularly with a language barrier. The floor uses all available means to establish intent — body language, a translator if available, reviewing the chip movement. When intent truly cannot be established, the floor applies the smaller action rule (call over raise) as a general principle, but makes the determination with full information.'
  },

  {
    id: 'sc_016', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: During the color-up, a player with 7 chips of the outgoing denomination has your chips counted and is given 1 new chip (worth the full 5 outgoing chips) and races for the remaining 2 odd chips. They win one chip in the race. How many new chips do they end up with after the color-up?',
    options: [
      'A) 1 new chip',
      'B) 2 new chips',
      'C) 3 new chips',
      'D) It depends on what the new chip denomination is'
    ],
    correct: 'B',
    explanation: 'The player had 7 old chips. 5 old chips exchange for 1 new chip (assuming 5:1 ratio). 2 old chips are the odd amount — they race for 2 chips and win 1. Result: 1 (from clean exchange) + 1 (won in race) = 2 new chips. They effectively receive value for 6 of their 7 old chips, losing 1 old chip\'s value in the race.'
  },

  {
    id: 'sc_017', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: Heads up, Player A (button/SB) puts in 500. Player B (BB) puts in 1,000. Player A raises to 3,000. Player B goes all-in for 4,200. Player A asks "is that a full raise?" The BB\'s additional raise was 1,200 over Player A\'s 3,000 raise. The minimum raise would have been 5,000. Is it a full raise and can Player A re-raise?',
    options: [
      'A) It is a full raise (50% rule applies: 1,200 is more than 50% of 2,000 increment) — Player A can re-raise',
      'B) It is not a full raise (needs 2,000 more to be minimum) — Player A can only call or fold',
      'C) It is always a full raise when a player goes all-in',
      'D) The 50% rule makes it a completed raise to 5,000 automatically'
    ],
    correct: 'B',
    explanation: 'Player A raised 2,000 (from 1,000 to 3,000). Minimum re-raise would be 5,000 (adding another 2,000). Player B went all-in for 4,200 — adding only 1,200 over Player A\'s 3,000. This is not a full raise (needs 2,000 more). Under most rules, the 50% rule applies to calling situations, not re-raises. Since it\'s sub-minimum, Player A cannot re-raise; they may only call 4,200 or fold.'
  },

  {
    id: 'sc_018', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: A player wins a hand and you push the pot. The player immediately says "wait — that pot is short." The dealer recounts and finds the pot is indeed 500 chips short of what it should have been. No one at the table acknowledges where the discrepancy occurred. What happens?',
    options: [
      'A) The player receives what was in the pot — the dealer cannot create chips',
      'B) The floor is called to investigate; if the source of the discrepancy cannot be found, the difference may come from the house reserve',
      'C) All players at the table contribute equally to make up the difference',
      'D) The hand is replayed from the beginning'
    ],
    correct: 'B',
    explanation: 'The floor investigates the discrepancy. If the source cannot be identified (most likely a player under-bet at some point without notice), the floor determines the resolution. In casinos, a house reserve may cover verified pot shortfalls. The hand cannot be replayed. The dealer calls the floor and does not guess or improvise a solution.'
  },

  {
    id: 'sc_019', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: During a tournament, you notice that one player\'s chip stack is significantly larger than it should be based on starting chips and the hands you\'ve seen. You suspect they may have added chips. What do you do?',
    options: [
      'A) Count their chips publicly to verify',
      'B) Ignore it — chip counts are verified at registration only',
      'C) Discreetly note your observation and report it to the floor immediately',
      'D) Ask the player directly where they got the extra chips'
    ],
    correct: 'C',
    explanation: 'Adding chips to a tournament stack is a serious violation (called "ratholing" or chip dumping in reverse). The dealer notes the observation and discreetly reports it to the floor. The floor can verify the stack count against records. A public confrontation or direct accusation by the dealer is inappropriate and could be wrong.'
  },

  {
    id: 'sc_020', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: The board reads A♠ K♥ Q♦ J♠ on the turn. Player A checks. Player B bets 1,000. Player A raises to 3,000. Player B goes all-in. Player A snap-calls and turns over T♠ 9♠. Player B tables A♥ K♦. The river is the 2♣. What does you announce and push?',
    options: [
      'A) "Straight, ten high — Player A wins"',
      'B) "Straight to the ace — Player A wins with ace-high straight"',
      'C) "Player B wins with two pair, aces and kings"',
      'D) "Player A wins with a straight flush"'
    ],
    correct: 'B',
    explanation: 'Player A: T♠ 9♠ with board A♠ K♥ Q♦ J♠ 2♣ = A-K-Q-J-T straight (ace-high straight). Player B: A♥ K♦ with board = two pair (aces and kings). Ace-high straight beats two pair. Correct announcement: "Straight, ace high — [Player A\'s name] wins." The dealer reads the best hand accurately.'
  },

  {
    id: 'sc_021', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: You are two hands from the money bubble. Table A finishes hands faster than Table B consistently, meaning players at Table A are blinded more than players at Table B. A player at Table A objects and demands the tables play at the same pace. What is the ruling?',
    options: [
      'A) Tables cannot be forced to play at the same pace — normal rules apply',
      'B) The TD may institute hand-for-hand play to equalize; that is the appropriate remedy',
      'C) Table A slows down until Table B catches up',
      'D) The slower table (B) is penalized for slowing the tournament'
    ],
    correct: 'B',
    explanation: 'Unequal blind posting due to table speed differentials near the bubble is the exact reason hand-for-hand play exists. If not already in effect, the player\'s concern is valid and the floor should be called. The TD decides whether to institute hand-for-hand play. Individual table pace cannot be mandatorily adjusted without this mechanism.'
  },
{
    id: 'sc_023', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: A player who is all-in has the best hand at showdown but their hole cards are discovered to be marked (small bent corner). The cards are from the house deck. What is the ruling?',
    options: [
      'A) The player loses their hand — marked cards are always the holder\'s responsibility',
      'B) The all-in player wins the pot; the deck is replaced; an investigation determines whether the marks were pre-existing or player-made',
      'C) The hand is void and all chips are returned',
      'D) The pot is split equally as a compromise'
    ],
    correct: 'B',
    explanation: 'If the marks are from the house deck (pre-existing wear), the player is not at fault and the hand result stands. The deck is immediately replaced. If investigation suggests the player made the marks, penalties up to disqualification apply. The pot is awarded based on the hand result while the investigation occurs.'
  },

  {
    id: 'sc_024', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: Player A raises all-in for 15,000. Player B calls. Player C calls. The dealer discovers Player A only had 14,800 (not 15,000) — your stack was miscounted before the hand. B and C both put in 15,000. What is the correct pot structure?',
    options: [
      'A) The main pot is 15,000×3=45,000 — Player A plays for the full amount',
      'B) The main pot is 14,800×3=44,400; the remaining 200 from B and C is returned to them',
      'C) The main pot is 14,800×3=44,400; the remaining 200 from B and C goes into a side pot they split',
      'D) The hand is void since the all-in amount was misrepresented'
    ],
    correct: 'B',
    explanation: 'The main pot is based on the actual all-in amount: 14,800×3=44,400. The 200 excess from Players B and C is returned to each of them (100 each). Player A plays for 44,400. The excess cannot form a side pot because there are only two callers and neither called relative to the other — they each put in the same excess amount.'
  },

  {
    id: 'sc_025', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: Players A and B are the last two at a final table. Player A (SB/Button) has 800,000 chips. Player B (BB) has 200,000. Player A raises all-in preflop. Player B calls all-in for 200,000. Player A shows A♠ K♦. Player B shows A♥ K♣. The board runs: A♦ K♥ 2♣ 7♦ 9♠. What does you announce and do?',
    options: [
      'A) "Chop pot — both players have two pair, aces and kings with the same kicker — split"',
      'B) "Player A wins with a better kicker" (K♦ vs K♣)',
      'C) "Both players play the board — split pot" (the board\'s best 5-card hand uses board cards only)',
      'D) "Player B wins — the big blind position gives the tiebreaker"'
    ],
    correct: 'A',
    explanation: 'Both players\' best 5-card hand: A♠/A♥ + K♦/K♣ + A♦ + K♥ + 9♠ = Two pair (A-A-K-K-9). Both hands are exactly identical in rank. It is a split pot. The suit of the K kicker is irrelevant — suits do not break ties in poker. Announce: "Split pot — identical hands."'
  },

  {
    id: 'sc_026', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: A player goes all-in preflop. The caller flips your cards face-up immediately (excited). Before the all-in player tables your cards, they see the caller\'s hand and say "I want to run it twice." The event rules allow run-it-twice. Must the all-in player be permitted to run it twice after seeing the caller\'s hand?',
    options: [
      'A) Yes — run-it-twice requests are always permitted when rules allow',
      'B) No — the all-in player saw the opponent\'s cards; allowing a run-twice request gives them decision-making leverage they should not have',
      'C) Yes but only if both players agree',
      'D) The floor decides based on whether the prematurely exposed hand affected the game'
    ],
    correct: 'B',
    explanation: 'When a player sees their opponent\'s hand before deciding to run it twice, they gain informational advantage (e.g., they\'re drawing thin and want more chances). This is unfair. The floor should deny the run-it-twice request in this specific scenario since the decision is influenced by improperly gained information. The prematurely exposed cards are also a separate floor issue.'
  },

  {
    id: 'sc_027', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: On the river, the board reads 5♦ 5♥ 5♣ 5♠ J♥. Player A says "I play the board" and tables Q♣ 2♦. Player B says "I play the board too" and tables K♠ 3♦. You begin to split the pot. Is this correct?',
    options: [
      'A) Yes — both players\' best hands are four fives with a jack; the pot splits',
      'B) No — Player B wins with four fives and a king kicker versus Player A\'s jack kicker',
      'C) No — Player A wins since Q♣ is higher than K♠... wait that\'s wrong',
      'D) No — Player B wins: four fives with K kicker beats four fives with J kicker'
    ],
    correct: 'D',
    explanation: 'Both players play four fives (from the board). Their fifth card (kicker) determines the winner. Player A\'s best hand: 5♦5♥5♣5♠ + Q♣ (queen kicker — higher than the J♥ on board). Player B\'s best hand: 5♦5♥5♣5♠ + K♠ (king kicker). King beats queen. Player B wins. Neither player "plays the board" — they both use their hole card as a kicker.'
  },

  {
    id: 'sc_028', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: A player verbally says "all-in" in a calm voice and moves all chips forward. Another player thinks it is a bluff and calls. A third player, who would have raised, says they were affected by the first player\'s calm demeanor. Can they claim angle shooting and get a ruling?',
    options: [
      'A) Yes — deliberately misleading behavior is an angle shoot',
      'B) No — acting in a calm manner is standard behavior; players can act however they choose as long as verbal declarations are honest',
      'C) Yes — if the TD determines the behavior was intentionally deceptive',
      'D) Yes — the third player can ask for a re-deal of the hand'
    ],
    correct: 'B',
    explanation: 'Players are permitted to act in any manner — calm, nervous, talkative, quiet — without it constituting an angle shoot. Only false verbal declarations or deceptive physical actions (acting out of turn to gain information) are penalizeable. Appearing calm while holding a strong hand is legitimate. The third player has no valid claim.'
  },

  {
    id: 'sc_029', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: The tournament is on the money bubble, hand-for-hand. All but two tables have completed your hands. One of the remaining tables has a player all-in and is waiting on the other table to finish. The TD approaches and says to deal out the remaining board cards quickly. Should the dealer comply?',
    options: [
      'A) Yes — the TD\'s instruction must be followed',
      'B) No — dealing cards faster than normal procedure does not change the outcome; standard procedure is always maintained',
      'C) Yes but deal each card with a 5-second pause to maintain some fairness',
      'D) Ask the all-in player\'s permission before complying with the TD'
    ],
    correct: 'B',
    explanation: 'The dealer maintains standard dealing procedure regardless of pace pressure. Rushing through the board to synchronize with another table\'s completion does not change card order. However, the TD has authority over pacing — the dealer should deal promptly but correctly. The cards that come are the cards; speed does not change them. Standard burn and turn procedure is always maintained.'
  },

  {
    id: 'sc_030', track: 'professional', category: 'scenarios', difficulty: 'hard',
    question: 'SCENARIO: Player A wins the tournament. During the awards, another player claims Player A was chip dumping to a friend who was eliminated at the final table. The tournament is over and prizes have been paid. What is the appropriate response from the dealer who dealt several final table hands?',
    options: [
      'A) The tournament is over — nothing can be done',
      'B) The dealer provides their honest recollection of any unusual hands to management if asked, as part of any investigation',
      'C) The dealer defends Player A if they saw nothing suspicious',
      'D) The dealer is not involved — this is management\'s problem only'
    ],
    correct: 'B',
    explanation: 'If management investigates a post-tournament allegation, the dealer provides their honest recollection of relevant hands when asked. The dealer neither advocates for nor against any player, and does not volunteer allegations of their own. Honest, objective recollection of observed events is the dealer\'s contribution to any integrity investigation.'
  },

];

export default SCENARIO_QUESTIONS;
